/** Split patterns keep each regex simple (Sonar complexity). */
export const DOCKER_LOG_ERROR_HINTS = [
  /\berror\b/,
  /\bcritical\b/,
  /\bfatal\b/,
  /exception/,
  /traceback/,
  /econnrefused/,
  /\berrno\b/,
  /\bpanic\b/,
  /segmentation/,
  /assertionerror/,
  /syntaxerror/,
  /importerror/,
  /modulenotfound/,
  /connection refused/,
  /address already in use/,
  /attributeerror/,
];
export const DOCKER_LOG_HTTP_ERROR_HINTS = [
  /\[admin\] could not fetch/,
  /http \d{3}.*\b(?:500|502|503|504)\b/,
  /" (?:500|502|503|504) /,
  /status[:\s]*(?:500|502|503|504)\b/,
];
export const DOCKER_LOG_WARNING_HINTS = [
  /\bwarn(?:ing)?\b/,
  /\bdeprecated\b/,
  /\b429\b/,
  /rate limit/,
  /retrying/,
  /slow query/,
  /timeout\b/,
  /timed out/,
  /non-zero exit/,
  /signal 9/,
  /oom/,
];
export const DOCKER_LOG_INFO_HINTS = [
  /\binfo\b/,
  /\bdebug\b/,
  /\blisten(?:ing)?\b/,
  /started/,
  /ready/,
  /healthy/,
  /" 200\b/,
  / 200 /,
  /\bok\b/,
  /success/,
  /completed/,
  /celery@/,
  /werkzeug/,
  /127\.0\.0\.1.*GET/,
];

/** Scan / investigation entry points — easy to spot in live Docker logs. */
export const DOCKER_LOG_SCAN_ENTRY_HINTS = [
  /\[SCAN START\]/i,
  /\[SCAN END\]/i,
  /GET \/aggregate-email-stream\b/i,
  /GET \/aggregate-stream-name\b/i,
  /GET \/aggregate-phone-stream\b/i,
  /GET \/aggregate-company-stream\b/i,
  /GET \/company-due-diligence\b/i,
  /GET \/holehe-email-stream/i,
  /GET \/blackbird_email_stream/i,
  /GET \/socialscan_email_stream/i,
  /GET \/profilehound-email-stream/i,
  /GET \/ghunt-email-stream/i,
  /GET \/googledork-email-stream/i,
  /\[SEARCH\] Starting parallel search/i,
  /\[GHUNT\] Starting scan/i,
  /Starting parallel search for:/i,
  /Enumerating registered subscriptions/i,
  /Running deep web dorking/i,
  /Running protected-platform SERP/i,
  /Querying Have I Been Pwned API/i,
  /\[ollama\]/i,
  /POST \/search\/preprocess\b/i,
  /\b\[frontend_trace\]/i,
];

export const LOG_LINE_CLASS = {
  scan_banner: 'text-fuchsia-100 border-l-fuchsia-400 bg-fuchsia-950/50 font-bold tracking-wide',
  scan_entry: 'text-cyan-200 border-l-cyan-400 bg-cyan-950/40 font-semibold',
  error: 'text-red-400 border-l-red-500/80 bg-red-950/20',
  warning: 'text-amber-300 border-l-amber-500/70 bg-amber-950/15',
  info: 'text-emerald-400 border-l-emerald-600/60',
  neutral: 'text-slate-300 border-l-slate-600/40',
  dimmed: 'text-slate-500 border-l-slate-700/30 opacity-50',
};

export const LOG_FILTER_MODES = ['all', 'scans', 'errors', 'warnings'];

const SCAN_MARKER_RE =
  /={5,}\s*\[SCAN\s+(START|END)\]\s+id=([^\s]+)\s+path=(\S+)(?:\s+target=(.+?)(?=\s+(?:bucket|elapsed_ms)=|\s*={5,}))?(?:\s+bucket=\S+)?(?:\s+elapsed_ms=(\d+))?\s*={5,}/i;

const FALLBACK_SCAN_START_RE =
  /GET\s+(\/(?:aggregate-email-stream|aggregate-stream-name(?:-relaxed)?|aggregate-phone-stream|aggregate-company-stream|aggregate-corporate-stream)[^"\s]*)/i;

/**
 * Parse a SCAN START/END banner from a Docker log line.
 *
 * @param {string} line
 * @returns {{ kind: 'start'|'end', id: string, shortId: string, path: string, target: string, elapsedMs: number|null }|null}
 */
export function parseScanMarker(line) {
  const text = String(line || '');
  const m = SCAN_MARKER_RE.exec(text);
  if (!m) return null;
  const id = String(m[2] || '').trim();
  const shortId = id.includes('-') ? id.split('-').pop().slice(0, 8) : id.slice(0, 8);
  return {
    kind: m[1].toUpperCase() === 'START' ? 'start' : 'end',
    id,
    shortId,
    path: String(m[3] || '').trim(),
    target: String(m[4] || '').trim().replace(/\s+=+$/, '').trim(),
    elapsedMs: m[5] ? Number(m[5]) : null,
  };
}

/**
 * Extract a fallback scan start from werkzeug access logs when banners are missing.
 *
 * @param {string} line
 * @param {number} [index]
 * @returns {{ kind: 'start', id: string, shortId: string, path: string, target: string, elapsedMs: null }|null}
 */
export function parseFallbackScanStart(line, index = 0) {
  const text = String(line || '');
  const m = FALLBACK_SCAN_START_RE.exec(text);
  if (!m) return null;
  const path = String(m[1] || '').split('?')[0];
  let target = '';
  const email = /[?&]email=([^&\s"]+)/i.exec(text);
  const name = /[?&]name=([^&\s"]+)/i.exec(text);
  const phone = /[?&]phone=([^&\s"]+)/i.exec(text);
  if (email) target = decodeURIComponent(email[1]);
  else if (name) target = decodeURIComponent(name[1]);
  else if (phone) target = decodeURIComponent(phone[1]);
  const id = `fallback-${index}-${path}-${target || 'scan'}`;
  return {
    kind: 'start',
    id,
    shortId: String(index),
    path,
    target,
    elapsedMs: null,
  };
}

/**
 * Group raw docker log lines into scan sessions for Admin Logs.
 *
 * Lines before the first scan go into an ``idle`` bucket. Concurrent scans
 * keep an open-session stack; unmarked lines attach to the latest open scan
 * (or stay idle).
 *
 * @param {string[]} lines
 * @returns {{ sessions: Array<object>, ordered: Array<{ type: 'banner'|'line', sessionId: string|null, line: string, index: number, marker?: object }> }}
 */
export function groupDockerLogsByScan(lines) {
  const sessions = [];
  const byId = new Map();
  const openStack = [];
  const ordered = [];

  const ensureSession = (marker, index) => {
    let sess = byId.get(marker.id);
    if (!sess) {
      sess = {
        id: marker.id,
        shortId: marker.shortId,
        path: marker.path,
        target: marker.target || '',
        startIndex: index,
        endIndex: null,
        elapsedMs: null,
        lineCount: 0,
      };
      byId.set(marker.id, sess);
      sessions.push(sess);
    } else {
      if (marker.target && !sess.target) sess.target = marker.target;
      if (marker.path && !sess.path) sess.path = marker.path;
    }
    return sess;
  };

  (lines || []).forEach((line, index) => {
    const explicit = parseScanMarker(line);
    const fallback = explicit ? null : parseFallbackScanStart(line, index);

    if (explicit && explicit.kind === 'start') {
      const sess = ensureSession(explicit, index);
      openStack.push(sess.id);
      sess.lineCount += 1;
      ordered.push({ type: 'banner', sessionId: sess.id, line, index, marker: explicit });
      return;
    }

    if (explicit && explicit.kind === 'end') {
      const sess = ensureSession(explicit, index);
      sess.endIndex = index;
      if (explicit.elapsedMs != null) sess.elapsedMs = explicit.elapsedMs;
      const idx = openStack.lastIndexOf(sess.id);
      if (idx >= 0) openStack.splice(idx, 1);
      sess.lineCount += 1;
      ordered.push({ type: 'banner', sessionId: sess.id, line, index, marker: explicit });
      return;
    }

    // Werkzeug access log: attach to open scan with same path/target — do not create a second session.
    if (fallback) {
      const openId = openStack.length ? openStack[openStack.length - 1] : null;
      const openSess = openId ? byId.get(openId) : null;
      const samePath = openSess && String(openSess.path || '') === String(fallback.path || '');
      const fbTarget = String(fallback.target || '').toLowerCase();
      const openTarget = String(openSess?.target || '').toLowerCase().replace(/\s*\(cache_hit\)\s*/g, '').trim();
      const sameTarget =
        !fbTarget || !openTarget || openTarget.includes(fbTarget) || fbTarget.includes(openTarget);
      if (openSess && samePath && sameTarget) {
        openSess.lineCount += 1;
        ordered.push({ type: 'line', sessionId: openSess.id, line, index });
        return;
      }
      const sess = ensureSession(fallback, index);
      openStack.push(sess.id);
      sess.lineCount += 1;
      ordered.push({ type: 'banner', sessionId: sess.id, line, index, marker: fallback });
      return;
    }

    // Attach by explicit [scan:xxxx] prefix when present.
    const prefix = /\[scan:([a-f0-9]{4,12})\]/i.exec(line);
    let sessionId = openStack.length ? openStack[openStack.length - 1] : null;
    if (prefix) {
      const short = prefix[1].toLowerCase();
      const match = sessions.find(
        (s) =>
          String(s.shortId || '').toLowerCase() === short ||
          String(s.id).toLowerCase().endsWith(short)
      );
      if (match) sessionId = match.id;
    }
    if (sessionId && byId.has(sessionId)) {
      byId.get(sessionId).lineCount += 1;
    }
    ordered.push({ type: 'line', sessionId, line, index });
  });

  return { sessions, ordered };
}

/**
 * @param {string} line
 * @param {string|null} selectedScanId
 * @param {ReturnType<typeof groupDockerLogsByScan>} grouped
 * @param {number} [lineIndex]
 * @returns {boolean}
 */
export function dockerLogMatchesScanSelection(line, selectedScanId, grouped, lineIndex) {
  if (!selectedScanId) return true;
  const row = grouped?.ordered?.[lineIndex];
  if (row?.sessionId === selectedScanId) return true;

  const sess = (grouped?.sessions || []).find((s) => s.id === selectedScanId);
  if (!sess) return false;

  const text = String(line || '');
  const lower = text.toLowerCase();
  const full = String(sess.id || '').toLowerCase();
  const short = String(sess.shortId || '').toLowerCase();

  // Explicit id on START/END banners or progress prefixes.
  if (full && lower.includes(full)) return true;
  if (short && /\[scan:([a-f0-9]{4,12})\]/i.test(text)) {
    const m = /\[scan:([a-f0-9]{4,12})\]/i.exec(text);
    if (m && m[1].toLowerCase() === short) return true;
  }
  // Banner kind for this id already handled via full includes; also match short in SCAN banners.
  if (short && /\[SCAN\s+(?:START|END)\]/i.test(text) && lower.includes(short)) return true;

  return false;
}

/**
 * Classify a docker log line for color / filter chips.
 *
 * @param {string} line
 * @returns {'scan_banner'|'scan_entry'|'error'|'warning'|'info'|'neutral'}
 */
export function dockerLogSeverity(line) {
  if (parseScanMarker(line)) {
    return 'scan_banner';
  }
  const lower = String(line || '').toLowerCase();
  if (DOCKER_LOG_SCAN_ENTRY_HINTS.some((re) => re.test(line))) {
    return 'scan_entry';
  }
  const isError =
    DOCKER_LOG_ERROR_HINTS.some((re) => re.test(lower)) ||
    DOCKER_LOG_HTTP_ERROR_HINTS.some((re) => re.test(lower));
  if (isError) {
    return 'error';
  }
  if (DOCKER_LOG_WARNING_HINTS.some((re) => re.test(lower))) {
    return 'warning';
  }
  if (DOCKER_LOG_INFO_HINTS.some((re) => re.test(lower))) {
    return 'info';
  }
  return 'neutral';
}

/**
 * @param {string} line
 * @param {'all'|'scans'|'errors'|'warnings'} mode
 * @returns {boolean}
 */
export function dockerLogMatchesFilter(line, mode) {
  const m = mode || 'all';
  if (m === 'all') return true;
  const sev = dockerLogSeverity(line);
  if (m === 'scans') return sev === 'scan_entry' || sev === 'scan_banner';
  if (m === 'errors') return sev === 'error';
  if (m === 'warnings') return sev === 'warning' || sev === 'error';
  return true;
}

/**
 * Case-insensitive substring match for the search box.
 *
 * @param {string} line
 * @param {string} query
 * @returns {boolean}
 */
export function dockerLogMatchesSearch(line, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return String(line || '').toLowerCase().includes(q);
}

/**
 * Split a log line into highlighted segments for search + scan-entry emphasis.
 *
 * @param {string} line
 * @param {string} searchQuery
 * @returns {Array<{ text: string, tone: 'default'|'match'|'scan_path'|'scan_query' }>}
 */
export function splitDockerLogHighlights(line, searchQuery = '') {
  const text = String(line || '');
  const segments = [];
  const q = String(searchQuery || '').trim();

  const pushSegment = (start, end, tone) => {
    if (start >= end) return;
    const slice = text.slice(start, end);
    if (!slice) return;
    const last = segments[segments.length - 1];
    if (last && last._end === start && last.tone === tone) {
      last.text += slice;
      last._end = end;
      return;
    }
    segments.push({ text: slice, tone, _end: end });
  };

  /** Collect non-overlapping highlight ranges sorted by start. */
  const ranges = [];

  const scanPathRe =
    /GET\s+\/(?:aggregate-email-stream|aggregate-stream-name[^"\s]*|aggregate-phone-stream|aggregate-company-stream|holehe-email-stream[^"\s]*|blackbird_email_stream[^"\s]*|socialscan_email_stream[^"\s]*|profilehound-email-stream[^"\s]*|ghunt-email-stream[^"\s]*|company-due-diligence[^"\s]*)/gi;
  let m;
  while ((m = scanPathRe.exec(text)) !== null) {
    ranges.push({ start: m.index, end: m.index + m[0].length, tone: 'scan_path' });
  }

  const scanIdRe = /\[scan:[a-f0-9]{4,12}\]|\[SCAN\s+(?:START|END)\]/gi;
  while ((m = scanIdRe.exec(text)) !== null) {
    ranges.push({ start: m.index, end: m.index + m[0].length, tone: 'scan_path' });
  }

  const emailParamRe = /(?:\?|&)email=[^"\s&]+|target=[^\s=]+/gi;
  while ((m = emailParamRe.exec(text)) !== null) {
    ranges.push({ start: m.index, end: m.index + m[0].length, tone: 'scan_query' });
  }

  if (q) {
    const lower = text.toLowerCase();
    const ql = q.toLowerCase();
    let idx = 0;
    while ((idx = lower.indexOf(ql, idx)) !== -1) {
      ranges.push({ start: idx, end: idx + ql.length, tone: 'match' });
      idx += ql.length;
    }
  }

  ranges.sort((a, b) => a.start - b.start || b.end - a.end - (a.end - a.start));
  const merged = [];
  for (const r of ranges) {
    const prev = merged[merged.length - 1];
    if (!prev || r.start >= prev.end) {
      merged.push({ ...r });
      continue;
    }
    if (r.end > prev.end) {
      prev.end = r.end;
      if (r.tone === 'scan_path') prev.tone = 'scan_path';
    }
  }

  let cursor = 0;
  for (const r of merged) {
    pushSegment(cursor, r.start, 'default');
    pushSegment(r.start, r.end, r.tone);
    cursor = r.end;
  }
  pushSegment(cursor, text.length, 'default');

  return segments.length ? segments.map(({ text: t, tone }) => ({ text: t, tone })) : [{ text, tone: 'default' }];
}

export const HIGHLIGHT_TONE_CLASS = {
  default: '',
  match: 'rounded bg-yellow-400/25 text-yellow-100 ring-1 ring-yellow-400/40',
  scan_path: 'rounded bg-violet-400/20 text-violet-100 font-bold ring-1 ring-violet-400/50',
  scan_query: 'rounded bg-sky-400/20 text-sky-100 ring-1 ring-sky-400/45',
};
